'use client';

import useSWR from 'swr';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

import { paths } from 'src/routes/paths';
import { NyttFärgTema, ChipFärger } from 'src/theme/NyttFärgtema';
import { hämtaStyrelseledamöter } from 'src/sections/styrelse/StyrelseService';
import { hämtaNästaStyrelsemöte } from 'src/sections/styrelse/StyrelseService';
import type { StyrelseledamotDto, StyrelsemöteDto } from 'src/types/styrelse';

import type { KortProps } from '../kort-katalog';
import DomainCard from '../components/domain-card';
import { MiniList, MiniRow, swrOptions } from './kort-primitiver';

// ----------------------------------------------------------------------

export default function StyrelseKort({ orgId }: KortProps) {
    const { data: ledamöter, isLoading: ledamöterLoading } = useSWR<StyrelseledamotDto[]>(
        orgId ? ['styrelseledamöter', orgId] : null,
        () => hämtaStyrelseledamöter(orgId),
        swrOptions
    );

    const { data: nästaMöte, isLoading: möteLoading } = useSWR<StyrelsemöteDto | null>(
        orgId ? ['nästa-styrelsemöte', orgId] : null,
        () => hämtaNästaStyrelsemöte(orgId),
        { ...swrOptions, shouldRetryOnError: false }
    );

    const loading = ledamöterLoading || möteLoading;
    const ledamöterLista = ledamöter ?? [];

    if (loading && ledamöterLista.length === 0) {
        return (
            <DomainCard
                title="Styrelsen"
                icon="solar:shield-user-bold"
                theme={NyttFärgTema.Kombinationer[6]}
                href={paths.dashboard.styrelse.root}
                primaryMetric={{ value: '—', label: 'ledamöter' }}
            >
                <Stack spacing={0.75}>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                </Stack>
            </DomainCard>
        );
    }

    if (ledamöterLista.length === 0) {
        return (
            <DomainCard
                title="Styrelsen"
                icon="solar:shield-user-bold"
                theme={NyttFärgTema.Kombinationer[6]}
                href={paths.dashboard.styrelse.root}
                emptyMessage="Lägg till styrelseledamöter för att se styrelsens sammansättning."
            />
        );
    }

    return (
        <DomainCard
            title="Styrelsen"
            icon="solar:shield-user-bold"
            theme={NyttFärgTema.Kombinationer[6]}
            href={paths.dashboard.styrelse.root}
            primaryMetric={{ value: ledamöterLista.length, label: ledamöterLista.length === 1 ? 'ledamot' : 'ledamöter' }}
            statusChip={
                nästaMöte?.föreslagenTidpunkt
                    ? { label: 'MÖTE PLANERAT', color: ChipFärger[6] }
                    : { label: 'INGET MÖTE', color: ChipFärger[8] }
            }
        >
            <MiniList>
                {ledamöterLista.slice(0, 4).map((l) => (
                    <MiniRow
                        key={l.id}
                        primary={[l.förnamn, l.efternamn].filter(Boolean).join(' ') || 'Ledamot'}
                        secondary={l.roll ?? undefined}
                    />
                ))}
            </MiniList>
        </DomainCard>
    );
}
